import { FC, useEffect, useMemo } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

import { ECareReceiversResultDict } from 'api/eCareContactNotes/eCareContactNotes.types';
import eCareCusComments from 'api/eCareCusComments/eCareCusComments';
import InfoIconWithTooltip from 'components/UI/atoms/InfoIconWithTooltip/InfoIconWithTooltip';
import FormInputCheckbox from 'components/UI/organisms/_formInputs/FormInputCheckbox/FormInputCheckbox';
import FormInputText from 'components/UI/organisms/_formInputs/FormInputText/FormInputText';
import QUERY_KEYS from 'constants/queryKeys/queryKeys';
import { Receivers } from 'constants/receiverDictionary/receiverDictionary';
import generalMessages from 'translations/common/general.mjs';
import cusComments from 'translations/specific/cusComments.mjs';

import useStyles from './CusCommentsDialog.styles';

type FormInput = {
  content: string;
  CUS: boolean;
  OPS: boolean;
  WYK: boolean;
  CT24: boolean;
};

export type Props = {
  orderId: string;
  serviceRecipientId: string;
  commentId?: string;
  isOpen: boolean;
  close: () => void;
  defaultValue?: FormInput;
  isNew?: boolean;
};

const FORM_ID = 'CusComments_form';

const CusCommentsDialog: FC<Props> = ({ orderId, serviceRecipientId, commentId, defaultValue, close, isOpen, isNew }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const { content, CUS, WYK, OPS, CT24 } = defaultValue || {};

  const { control, getValues, handleSubmit, reset } = useForm<FormInput>({
    defaultValues: {
      content: content || '',
      CUS: isNew ? true : CUS,
      OPS: isNew ? true : OPS,
      WYK: isNew ? true : WYK,
      CT24: isNew ? true : CT24,
    },
  });

  useEffect(() => reset(defaultValue), [defaultValue]);

  const queryKey = useMemo(() => [QUERY_KEYS.GET_ECARE_CUS_COMMENTS, orderId], [orderId]);
  const queryClient = useQueryClient();

  const createComment = eCareCusComments.createComment();
  const updateComment = eCareCusComments.patchCommentData(commentId as string);

  const statusMutation = useMutation(isNew ? createComment : updateComment, {
    onMutate: async () => {
      await queryClient.cancelQueries(queryKey);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries(queryKey);
    },
  });

  const receiversResult = (formData: { [key: string]: boolean | string }) =>
    Object.keys(formData).filter(key => typeof formData[key] === 'boolean' && formData[key] === true);

  const isValidReceivers = () => {
    const formData: FormInput = getValues();
    return receiversResult(formData).length > 0;
  };

  const onSave = async (formData: FormInput) => {
    await statusMutation.mutateAsync({
      content: formData.content as string,
      service_recipient: serviceRecipientId,
      receivers: receiversResult(formData) as Receivers[],
      order: orderId,
    });

    reset();
    close();
  };

  return (
    <Dialog
      aria-describedby='alert-dialog-description'
      aria-labelledby='alert-dialog-title'
      fullWidth
      maxWidth='sm'
      open={isOpen}
      onClose={close}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography className={classes.dialogTitleText} component='div' variant='h2'>
          {t(cusComments.addComment)}
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <DialogContentText id='alert-dialog-description'>
          <form id={FORM_ID} onSubmit={handleSubmit(onSave)}>
            <FormInputText control={control} label={t(cusComments.titleField)} multiline name='content' required rows={6} />
            <div className={classes.filedTitle}>{t(cusComments.receiverTitle)}</div>
            <div className={classes.checkboxWrapper}>
              {ECareReceiversResultDict.map(callInitiator => (
                <FormInputCheckbox
                  control={control}
                  key={callInitiator}
                  label={callInitiator}
                  name={callInitiator}
                  rules={{
                    validate: () => isValidReceivers() || t(cusComments.form.receiverError),
                  }}
                />
              ))}
              <InfoIconWithTooltip content={t(cusComments.tooltip.receiver)} wrapperProps={{ sx: { ml: 1 } }} />
            </div>
            <div>{t(cusComments.form.receiverHelperText)}</div>
          </form>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={close}>
          {t(generalMessages.cancel)}
        </Button>
        <Button autoFocus variant='contained' onClick={handleSubmit(onSave)}>
          {t(generalMessages.save)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CusCommentsDialog;
